/**
 * Utility functions for working with option arrays and mappings
 */

/**
 * Generic option type with value and label
 */
export interface Option {
  value: string;
  label: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Gets the label for a given value from an array of options
 * @param options - Array of options with {value, label} structure
 * @param value - The value to look up
 * @returns The label if found, otherwise returns the value itself
 */
export const getLabelByValue = (options: Option[], value: string): string => {
  const option = options.find(opt => opt.value === value);
  return option?.label || value;
};

/**
 * Gets the full option object for a given value
 * @param options - Array of options with {value, label} structure
 * @param value - The value to look up
 * @returns The option object if found, otherwise null
 */
export const getOptionByValue = (options: Option[], value: string): Option | null => {
  return options.find(opt => opt.value === value) || null;
};

/**
 * Gets the label for a value from a nested mapping structure
 * Used for mappings like PRODUCT_FAMILY_GL_MAPPING where parent key maps to array of options
 * @param mapping - Nested mapping object where keys map to arrays of options
 * @param parentKey - The parent key to look up in the mapping
 * @param value - The value to find within the parent's options array
 * @returns The label if found, otherwise returns the value itself
 */
export const getNestedLabelByValue = (
  mapping: Record<string, Option[]>,
  parentKey: string,
  value: string
): string => {
  if (!parentKey || !mapping[parentKey]) {
    return value;
  }
  
  const option = mapping[parentKey].find(opt => opt.value === value);
  return option?.label || value;
};

/**
 * Maps an array of values to their corresponding labels
 * @param options - Array of options with {value, label} structure
 * @param values - Array of values to map
 * @returns Array of labels corresponding to the values
 */
export const mapValuesToLabels = (options: Option[], values: string[]): string[] => {
  return values.map(value => getLabelByValue(options, value));
};
